// Presentational contact modal — email / live chat / WhatsApp / (phone) rows.
// Ported from the original ContactModal; the only change is that the live-chat
// action is injected (`onLiveChat` + `liveChatLoading`) instead of calling
// useIntercom() directly, so the same layout serves both the Intercom and the
// Ella arm (see ContactModal.tsx, the dispatcher). Market-aware bits (phone /
// WhatsApp per market) are unchanged.

import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BookingLanguage } from "@/types/booking";
import {
  emailIcon,
  liveChatIcon,
  phoneIcon,
  whatsappIcon,
} from "@/assets/images";
import { languageMap } from "@/i18n";
import { useMarket } from "@/hooks/useMarket";
import { getSupportPhone, getWhatsAppSupportUrl } from "@/config/market";

export interface ContactModalViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  language: BookingLanguage;
  /** Opens the active live-chat backend (Intercom or Ella). */
  onLiveChat: () => void;
  /** Show the spinner on the live-chat row while it boots (Intercom only). */
  liveChatLoading: boolean;
}

const ContactModalView = ({
  open,
  onOpenChange,
  language,
  onLiveChat,
  liveChatLoading,
}: ContactModalViewProps) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { market } = useMarket();
  // Async-first markets answer on their own WhatsApp and have no phone line —
  // don't offer the Swiss number there (AHD-8731).
  const supportPhone = getSupportPhone(market);

  const contactMethods = [
    {
      icon: emailIcon,
      title: t("contact.emailUs"),
      subtitle: "get@aheadhealth.com",
      onClick: () => (window.location.href = "mailto:get@aheadhealth.com"),
    },
    {
      icon: liveChatIcon,
      title: t("contact.liveChat"),
      subtitle: t("contact.startsLiveChat"),
      onClick: onLiveChat,
    },
    {
      icon: whatsappIcon,
      title: t("contact.whatsAppUs"),
      subtitle: t("contact.startWhatsAppChat"),
      onClick: () => window.open(getWhatsAppSupportUrl(market), "_blank"),
    },
  ];

  if (supportPhone) {
    contactMethods.push({
      icon: phoneIcon,
      title: t("contact.callUs"),
      subtitle: supportPhone,
      onClick: () =>
        (window.location.href = `tel:${supportPhone.replace(/\s/g, "")}`),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl sm:rounded-lg">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-medium text-gray-900">
            {t("contact.title")}
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-1">{t("contact.subtitle")}</p>
        </DialogHeader>

        <div className="mt-2">
          {contactMethods.map((method, index) => {
            const isLiveChat = index === 1; // Live chat is the second item
            const isDisabled = isLiveChat && liveChatLoading;

            return (
              <div key={index}>
                <button
                  onClick={method.onClick}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-4 py-4 px-2 transition-colors text-left group ${
                    isDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <div className="flex-shrink-0">
                    <div
                      className="w-12 h-12 rounded-md flex items-center justify-center"
                      style={{ backgroundColor: "#93B6EE" }}
                    >
                      <img
                        src={method.icon}
                        alt={method.title}
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-base font-normal text-gray-900 normal-case "
                      style={{ fontSize: 19 }}
                    >
                      {method.title}
                    </h3>
                    <p className="text-sm text-gray-900 normal-case underline">
                      {method.subtitle}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    {isDisabled ? (
                      <svg
                        className="w-5 h-5 text-gray-400 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </div>
                </button>
                {index < contactMethods.length - 1 && (
                  <div className="border-b border-gray-100"></div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModalView;
